import type { RequestOptions, RestClient } from '@directus/sdk';
import type {
  TGetDepartmentRowsRequestBody,
  TGetDepartmentRowsResponse,
} from '@sediwork/shared/types/api/distribute/getDepartmentRows';
import type { TGetFacilityTreeResponse } from '@sediwork/shared/types/api/facilityTree/getFacilityTree';
import type {
  TGetPlanningGroupResponse,
  TGetPlanningGroupsRequest,
  TGetPlanningGroupsResponse,
} from '@sediwork/shared/types/api/distribute/getPlanningGroups';
import type { TGetServiceGroupsRequest, TGetServiceGroupsResponse } from '@sediwork/shared/types/api/distribute/getServiceGroups';
import type {
  TGetUserResponse,
  TGetUsersRequest,
  TGetUsersResponse,
} from '@sediwork/shared/types/api/distribute/getUsers';
import type {
  TGetWorkloadsMapRequest,
  TGetWorkloadsMapResponse,
} from '@sediwork/shared/types/api/distribute/getWorkloads';
import type {
  TChatRequest,
  TChatResponse,
  TChatThreadDetailResponse,
  TChatThreadListResponse,
} from '@sediwork/shared/types/api/llm-chat/chat';
import type { TEmptyObject, TUuid } from '@sediwork/shared/types/common';

type TRequestOptionsBase = Pick<RequestOptions, 'path'>;

type TRequestGetOptions<T> = TRequestOptionsBase & Readonly<{
  method: 'GET'
  params: T
}>;

type TRequestPostOptions<T> = TRequestOptionsBase & Readonly<{
  method: 'POST'
  body: T
}>;

type TRequestDeleteOptions = TRequestOptionsBase & Readonly<{
  method: 'DELETE'
}>;

type TRequestOptions<T> =
  | TRequestGetOptions<T>
  | TRequestPostOptions<T>
  | TRequestDeleteOptions;

export class Api<SCHEMA> {
  constructor (private client: RestClient<SCHEMA>) {}

  private request<REQ extends object = TEmptyObject, RES = void>(
    options: TRequestOptions<REQ>,
  ) {
    let body = undefined;
    const rawBody = options.method === 'POST' && options.body;
    if (rawBody && typeof rawBody !== 'string') {
      try {
        body = JSON.stringify(rawBody);
      } catch {
        // Continue regardless of errors
      }
    }

    return this.client.request<RES>(() => ({ ...options, body }));
  }

  // ToDo: Make it a GET request once we only have primitive types as parameters
  public getInternalUsers = (
    body: TGetUsersRequest,
  ) => this.request<TGetUsersRequest, TGetUsersResponse>({
    method: 'POST',
    path: '/distribute/users/internal',
    body,
  });

  // ToDo: Make it a GET request once we only have primitive types as parameters
  public getExternalUsers = (
    body: TGetUsersRequest,
  ) => this.request<TGetUsersRequest, TGetUsersResponse>({
    method: 'POST',
    path: '/distribute/users/external',
    body,
  });

  // ToDo: Make it a GET request once we only have primitive types as parameters
  public getUser = (
    userId: TUuid,
    body: TGetUsersRequest,
  ) => this.request<TGetUsersRequest, TGetUserResponse>({
    method: 'POST',
    path: `/distribute/users/${ userId }`,
    body,
  });

  public getUserWorkloads = (
    body: TGetWorkloadsMapRequest,
  ) => this.request<TGetWorkloadsMapRequest, TGetWorkloadsMapResponse>({
    method: 'POST',
    path: '/distribute/workloads',
    body,
  });

  public getFacilityTree = () => this.request<TEmptyObject, TGetFacilityTreeResponse>({
    method: 'GET',
    path: '/distribute/facilityTree',
    params: {},
  });

  public getDepartmentRows = (
    body: TGetDepartmentRowsRequestBody,
  ) => this.request<TGetDepartmentRowsRequestBody, TGetDepartmentRowsResponse>({
    method: 'POST',
    path: '/distribute/departmentRows',
    body,
  });

  public getDepartmentRow = (
    elementId: TUuid,
    body: TGetDepartmentRowsRequestBody,
  ) => this.request<TGetDepartmentRowsRequestBody, TGetDepartmentRowsResponse>({
    method: 'POST',
    path: `/distribute/departmentRows/${elementId}`,
    body,
  });

  public getServiceGroups = (
    params: TGetServiceGroupsRequest,
  ) => this.request<TGetServiceGroupsRequest, TGetServiceGroupsResponse>({
    method: 'GET',
    path: '/distribute/servicegroups',
    params,
  });

  public getPlanningGroups = (
    params: TGetPlanningGroupsRequest,
  ) => this.request<TGetPlanningGroupsRequest, TGetPlanningGroupsResponse>({
    method: 'GET',
    path: '/distribute/planningGroups/',
    params,
  });

  public getPlanningGroup = (
    planningGroupId: TUuid,
  ) => this.request<TEmptyObject, TGetPlanningGroupResponse>({
    method: 'GET',
    path: `/distribute/planningGroups/${ planningGroupId }`,
    params: {},
  });

  public getChatCompletion = (
    body: TChatRequest,
  ) => this.request<TChatRequest, TChatResponse>({
    method: 'POST',
    path: '/llm-chat/completion',
    body,
  });

  public getChatThreads = () => this.request<TEmptyObject, TChatThreadListResponse>({
    method: 'GET',
    path: '/llm-chat/threads',
    params: {},
  });

  public getChatThread = (
    threadId: TUuid,
  ) => this.request<TEmptyObject, TChatThreadDetailResponse>({
    method: 'GET',
    path: `/llm-chat/threads/${ threadId }`,
    params: {},
  });

  public deleteChatThread = (
    threadId: TUuid,
  ) => this.request({
    method: 'DELETE',
    path: `/llm-chat/threads/${ threadId }`,
  });
}
